import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
  };

  const showSolid = !isHome || scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        showSolid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 group" onClick={closeMobileMenu}>
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-base"></i>
            </div>
            <span className={`font-heading text-lg font-semibold transition-colors ${
              showSolid ? 'text-foreground-950' : 'text-background-50'
            }`}>
              Superbuy Spread
            </span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary-500 ${
              showSolid ? 'text-foreground-700' : 'text-background-50/90'
            }`}
          >
            Home
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${
                showSolid ? 'text-foreground-700' : 'text-background-50/90'
              }`}
            >
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[520px] bg-background-50/95 backdrop-blur-lg rounded-xl border border-background-200/40 shadow-lg p-5 grid grid-cols-2 gap-1.5 z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    <div className="w-6 h-6 rounded-md bg-primary-100/60 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-primary-500 text-xs"></i>
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/article/superbuy-spreadsheet-guide"
            className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${
              showSolid ? 'text-foreground-700' : 'text-background-50/90'
            }`}
          >
            Guide
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${
              showSolid ? 'text-foreground-600' : 'text-background-50/80'
            }`}
          >
            Superbuy Sheet
            <i className="ri-external-link-line text-xs"></i>
          </a>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-line text-sm"></i>
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              showSolid
                ? 'text-foreground-700 hover:bg-background-200'
                : 'text-background-50 hover:bg-background-50/10'
            }`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={closeMobileMenu}
        ></div>

        <div
          className={`absolute top-0 right-0 h-full w-[320px] max-w-[85vw] bg-background-50 shadow-lg transition-transform duration-300 flex flex-col ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-5 h-16 border-b border-background-200/60 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2" onClick={closeMobileMenu}>
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading text-base font-semibold text-foreground-950">Superbuy Spread</span>
            </Link>
            <button
              onClick={closeMobileMenu}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-6">
            <div className="space-y-1 mb-8">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-line text-foreground-600 text-lg"></i>
                </div>
                Home
              </Link>
              <Link
                to="/article/superbuy-spreadsheet-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-foreground-600 text-lg"></i>
                </div>
                Ultimate Guide
              </Link>
              <Link
                to="/article/superbuy-spreadsheet-safety"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                </div>
                Safety Guide
              </Link>
              <Link
                to="/article/superbuy-spreadsheet-tips"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-secondary-600 text-lg"></i>
                </div>
                Shopping Tips
              </Link>
              <Link
                to="/article/superbuy-spreadsheet-comparison"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-scales-3-line text-primary-600 text-lg"></i>
                </div>
                Comparison
              </Link>
            </div>

            <div className="mb-8">
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                    <i className="ri-grid-line text-foreground-600 text-lg"></i>
                  </div>
                  <span>Product Categories</span>
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  mobileCategoryOpen ? 'max-h-[1200px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="grid grid-cols-1 gap-0.5 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-primary-500 text-xs"></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-4 border-t border-background-200/60">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-600 hover:bg-background-100 transition-colors text-sm font-medium"
              >
                <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-external-link-line text-foreground-600 text-lg"></i>
                </div>
                Superbuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-xl hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}